import { httpsCallable } from 'firebase/functions'
import { functions } from '@/lib/firebase'

/**
 * Authoritative web-access check.
 *
 * Reuses the deployed `getSubscriptionStatus` callable (the same endpoint the
 * /account portal uses). Server-side it reads the LIVE RevenueCat entitlement
 * (covering App Store / Play / Paddle, and trials) and classifies the provider:
 *   - managedBy 'store'  → active store subscription
 *   - managedBy 'paddle' → a resolvable Paddle subscription — NOT necessarily
 *     active! The server intentionally resolves EXPIRED subs too (via the
 *     sticky paddleSubscriptionTransferred flag) so the /account portal can
 *     show a canceled sub and offer resubscribe. Check subscription.status.
 *   - managedBy 'none'   → not entitled
 */
type SubscriptionStatusResult =
  | { managedBy: 'paddle'; subscription: { status: string } }
  | { managedBy: 'store'; store: string | null }
  | { managedBy: 'none' }

/** Paddle statuses that mean "currently entitled" (past_due = payment grace). */
const ACTIVE_PADDLE_STATUSES = ['active', 'trialing', 'past_due']

export async function fetchWebAccess(): Promise<boolean> {
  const fn = httpsCallable<Record<string, never>, SubscriptionStatusResult>(
    functions,
    'getSubscriptionStatus',
  )
  const res = (await fn({})).data
  if (!res || res.managedBy === 'none') return false
  if (res.managedBy === 'paddle') return ACTIVE_PADDLE_STATUSES.includes(res.subscription?.status)
  // store-managed: only returned for live entitlements (or RC-outage fail-open,
  // matching the server's isPremiumVerified behavior on paid functions).
  return true
}
